import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { getVoicePref } from "@/lib/voice";
import { playUrl, stopAudio, ttsUrl, speakFallback } from "@/lib/audio";
import { playAlarm } from "@/lib/alarm";
import { useAuth } from "@/context/AuthContext";
import { VoiceWave } from "@/components/Bubbles";
import { Pill, Check, Clock, Volume2, X } from "lucide-react";

const SLOT_HOURS = { matin: [8, 0], midi: [12, 30], soir: [20, 0] };
const SLOT_LABEL = { matin: "ce matin", midi: "ce midi", soir: "ce soir" };
const day = () => new Date().toISOString().slice(0, 10);
const isEnabled = () => localStorage.getItem("omy_health_voice") !== "0";

const SNOOZES = [
  { label: "10 min", ms: 10 * 60000 },
  { label: "30 min", ms: 30 * 60000 },
  { label: "1 heure", ms: 60 * 60000 },
  { label: "2 heures", ms: 120 * 60000 },
];

export function HealthVoiceReminder() {
  const { user } = useAuth();
  const firstName = (user?.name || "").split(" ")[0] || "";
  const [due, setDue] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro | reveal | snooze
  const [snoozeAt, setSnoozeAt] = useState(() => localStorage.getItem("omy_snooze_hhmm") || "20:00");
  const [speaking, setSpeaking] = useState(false);
  const timerRef = useRef(null);
  const dueRef = useRef(null);

  const revealPhrase = (d) => `C'est l'heure de prendre ${d.name}${d.dosage ? ", " + d.dosage : ""}.`;
  const introPhrase = () => `Hey ${firstName}, j'ai un rappel important pour toi. Veux-tu le connaître maintenant, ou plus tard ?`;

  const speak = async (text) => {
    setSpeaking(true);
    try {
      await playUrl(ttsUrl(text, getVoicePref()));
      setSpeaking(false);
    } catch (e) {
      setSpeaking(false);
      speakFallback(text, { onstart: () => setSpeaking(true), onend: () => setSpeaking(false) });
    }
  };

  const notify = (d) => {
    try {
      if (!("Notification" in window)) return;
      const show = () => new Notification("OMY — Rappel important", {
        body: `C'est l'heure de prendre ${d.name}${d.dosage ? ` (${d.dosage})` : ""}.`,
        tag: `omy-med-${d.medication_id}-${d.slot}`, requireInteraction: true,
      });
      if (Notification.permission === "granted") show();
      else if (Notification.permission !== "denied") Notification.requestPermission().then((p) => { if (p === "granted") show(); });
    } catch (e) {}
  };

  const check = async () => {
    if (!isEnabled() || dueRef.current) return;
    let data;
    try { const r = await api.get("/health/today"); data = r.data; } catch (e) { return; }
    const now = new Date();
    const d0 = day();
    for (const d of (data.doses || [])) {
      if (d.status) continue;
      const hm = SLOT_HOURS[d.slot]; if (!hm) continue;
      const dueTime = new Date(now); dueTime.setHours(hm[0], hm[1], 0, 0);
      if (now < dueTime) continue;
      if (d.snoozed_until && Date.now() < new Date(d.snoozed_until).getTime()) continue;
      const key = `omy_medvoice_${d0}_${d.medication_id}_${d.slot}`;
      const flag = localStorage.getItem(key);
      if (flag === "done") continue;
      if (flag && Date.now() < parseInt(flag)) continue;
      localStorage.setItem(key, String(Date.now() + 15 * 60000)); // re-fire in 15min if ignored
      dueRef.current = d;
      setDue(d); setPhase("intro");
      playAlarm();
      notify(d);
      speak(introPhrase());
      return;
    }
  };

  useEffect(() => {
    check();
    timerRef.current = setInterval(check, 60000);
    return () => { clearInterval(timerRef.current); stopAudio(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!due) return null;

  const close = () => { stopAudio(); dueRef.current = null; setDue(null); };

  const goReveal = () => { setPhase("reveal"); speak(revealPhrase(due)); };

  const snooze = async (ms) => {
    const key = `omy_medvoice_${day()}_${due.medication_id}_${due.slot}`;
    const until = new Date(Date.now() + ms).toISOString();
    localStorage.setItem(key, String(Date.now() + ms));
    try { await api.post(`/health/medications/${due.medication_id}/snooze`, { slot: due.slot, until }); } catch (e) {}
    close();
  };

  const snoozeUntil = (hhmm) => {
    const [h, m] = (hhmm || "").split(":").map(Number);
    if (isNaN(h)) return;
    localStorage.setItem("omy_snooze_hhmm", hhmm);
    const now = new Date();
    const target = new Date(now); target.setHours(h, m || 0, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    snooze(target.getTime() - now.getTime());
  };

  const take = async () => {
    try { await api.post(`/health/medications/${due.medication_id}/log`, { slot: due.slot, status: "taken" }); } catch (e) {}
    localStorage.setItem(`omy_medvoice_${day()}_${due.medication_id}_${due.slot}`, "done");
    close();
  };

  const slotLabel = SLOT_LABEL[due.slot] || "";

  return (
    <div data-testid="health-voice-reminder" className="fixed inset-0 z-[60] flex items-end justify-center sm:justify-end p-4 pointer-events-none">
      <div className="absolute inset-0 bg-black/20 pointer-events-auto" onClick={() => snooze(15 * 60000)} />
      <div className="omy-glass rounded-3xl p-6 w-full max-w-sm text-center relative pointer-events-auto shadow-2xl animate-fadeUp">
        <button data-testid="health-voice-close" onClick={() => snooze(15 * 60000)} title="Plus tard"
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X size={18} /></button>

        <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
          style={{ background: "linear-gradient(135deg,#E05A36,#F59E0B)" }}>
          <Pill size={28} className="text-white" />
        </div>

        <div className="flex items-center justify-center gap-2 mb-1">
          <span className="font-mono text-[10px] uppercase tracking-widest text-primary">Rappel santé</span>
          {speaking && <VoiceWave active bars={4} />}
        </div>

        {phase === "intro" && (
          <>
            <h2 className="font-display text-xl font-semibold">Rappel important</h2>
            <p className="text-sm text-foreground/80 mt-2 mb-5">
              Hey {firstName || "toi"}, j'ai un rappel important pour toi. Veux-tu le connaître maintenant, ou plus tard ?
            </p>
            <div className="flex gap-2">
              <button data-testid="health-remind-now" onClick={goReveal}
                className="flex-1 h-11 rounded-full bg-primary hover:bg-primary/90 text-white font-medium">Maintenant</button>
              <button data-testid="health-remind-later" onClick={() => setPhase("snooze")}
                className="flex-1 h-11 rounded-full bg-white/10 hover:bg-white/15 font-medium">Plus tard</button>
            </div>
          </>
        )}

        {phase === "reveal" && (
          <>
            <h2 className="font-display text-xl font-semibold">C'est l'heure de ta prise</h2>
            <p className="text-sm text-foreground/85 mt-2 mb-5">
              Prends <span className="font-semibold text-foreground">{due.name}</span>{due.dosage ? ` (${due.dosage})` : ""} {slotLabel}.
            </p>
            <div className="flex items-center justify-center gap-2 mb-4">
              <button data-testid="health-voice-replay" onClick={() => speak(revealPhrase(due))}
                className="text-primary hover:scale-110 transition-transform" aria-label="Réécouter"><Volume2 size={18} /></button>
            </div>
            <div className="flex gap-2">
              <button data-testid="health-voice-taken" onClick={take}
                className="flex-1 h-11 rounded-full bg-emerald-500/90 hover:bg-emerald-500 text-white font-medium flex items-center justify-center gap-1.5"><Check size={16} /> Pris</button>
              <button data-testid="health-voice-later" onClick={() => setPhase("snooze")}
                className="flex-1 h-11 rounded-full bg-white/10 hover:bg-white/15 font-medium flex items-center justify-center gap-1.5"><Clock size={16} /> Reporter</button>
            </div>
          </>
        )}

        {phase === "snooze" && (
          <>
            <h2 className="font-display text-xl font-semibold">Plus tard, quand ?</h2>
            <p className="text-sm text-foreground/70 mt-2 mb-4">Je te le rappellerai dans…</p>
            <div className="grid grid-cols-2 gap-2">
              {SNOOZES.map((s) => (
                <button key={s.label} data-testid={`health-snooze-${s.ms}`} onClick={() => snooze(s.ms)}
                  className="h-11 rounded-full bg-white/8 hover:bg-primary/20 hover:text-primary border border-white/10 font-medium">{s.label}</button>
              ))}
            </div>
            <div className="mt-3 rounded-2xl bg-black/25 border border-white/10 p-3 text-left">
              <p className="text-xs text-muted-foreground mb-2">…ou reprogramme à une heure précise</p>
              <div className="flex items-center gap-2">
                <input data-testid="health-snooze-time" type="time" value={snoozeAt}
                  onChange={(e) => setSnoozeAt(e.target.value)}
                  className="flex-1 h-10 rounded-full bg-black/40 border border-white/10 px-4 text-sm outline-none focus:border-primary/50" />
                <button data-testid="health-snooze-at" onClick={() => snoozeUntil(snoozeAt)}
                  className="h-10 px-4 rounded-full bg-primary hover:bg-primary/90 text-white text-sm font-medium shrink-0">Reprogrammer</button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Cette heure sera proposée par défaut la prochaine fois.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
